/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { isPendingPublicationStatus } from '@/shared/utils/portal-publish';
import {
  FF_APP_CHAVES_NA_MAO, FF_APP_FACEBOOK, FF_APP_OLX, FF_APP_PORTAL_FEEDS, FF_APP_PORTALS_INTEGRATION,
} from '@/shared/utils/feature-flag';
import type { PortalPublication, PublicationRow } from './types';

const PORTAL_KEYS = new Set(['olx', 'zap', 'vivareal', 'chavesnamao']);

const INDEX_TO_ACTION: Record<string, string> = {
  facebook_feed: 'facebook',
  facebook_marketplace: 'facebook',
  facebook_lead_ad: 'facebook',
};

/**
 * Maps a publications-catalog portal id (e.g. 'facebook_feed') onto the
 * portal id this row-building/action layer understands ('facebook'). The
 * publish flow enqueues jobs under the catalog's finer-grained ids, but the
 * raw publish-status payload consumed here is keyed by whatever id was
 * enqueued -- normalize before any lookup so a Facebook Feed/Marketplace/Lead
 * Ad result doesn't fall through to an unlabeled generic row with no
 * edit/view-post support.
 */
export function actionPortalForIndex(indexPortal: string): string {
  return INDEX_TO_ACTION[indexPortal] ?? indexPortal;
}

/**
 * Resolves the id arrays for a publication, falling back to single-id fields.
 */
/** The Lead Ad outcome of a publish, spread onto the rows of that publish. */
export function leadAdFields(
  pub: PortalPublication | undefined,
): Pick<PublicationRow, 'lead_ad_status' | 'lead_ad_error'> {
  if (!pub?.lead_ad_status) return {};
  return {
    lead_ad_status: pub.lead_ad_status,
    ...(pub.lead_ad_error ? { lead_ad_error: pub.lead_ad_error } : {}),
  };
}

/**
 * Resolve ids.
 */
function resolveIds(pub: PortalPublication): { postIds: string[]; listingIds: string[] } {
  const postIds = pub.post_ids ?? (pub.post_id ? [pub.post_id] : []);
  const listingIds = pub.listing_ids ?? (pub.listing_id ? [pub.listing_id] : []);
  return { postIds, listingIds };
}

/**
 * Builds a facebook row label, appending an index suffix when multiple rows exist.
 */
function facebookLabel(base: string, multiple: boolean, suffix: string): string {
  return multiple ? `${base} (${suffix})` : base;
}

/**
 * Placeholder row while async publish has no external ids yet (or failed).
 */
function pendingPortalRow(
  portal: string,
  pub: PortalPublication,
  getPortalLabel: (p: string) => string,
  key = `${portal}-pending`,
): PublicationRow[] {
  if (!isPendingPublicationStatus(pub.status)) return [];
  return [{
    key,
    portal,
    label: getPortalLabel(portal),
    status: pub.status,
    published_at: pub.published_at,
    ...leadAdFields(pub),
  }];
}

/**
 * Builds the publication rows for Facebook.
 */
function buildFacebookRows(pub: PortalPublication, getPortalLabel: (p: string) => string): PublicationRow[] {
  const portal = 'facebook';
  const { postIds, listingIds } = resolveIds(pub);
  if (postIds.length === 0 && listingIds.length === 0) {
    return pendingPortalRow(portal, pub, getPortalLabel);
  }
  const multiple = postIds.length + listingIds.length > 1;
  const base = getPortalLabel(portal);
  const postRows = postIds.map((pid, i) => ({
    key: `facebook-post-${pid}`,
    portal,
    label: facebookLabel(base, multiple, `Post ${i + 1}`),
    status: pub.status,
    published_at: pub.published_at,
    post_id: pid,
    ad_id: pub.ad_id,
    ...leadAdFields(pub),
  }));
  const listingRows = listingIds.map((lid, i) => ({
    key: `facebook-listing-${lid}`,
    portal,
    label: facebookLabel(base, multiple, `Listing ${i + 1}`),
    status: pub.status,
    published_at: pub.published_at,
    listing_id: lid,
  }));
  return [...postRows, ...listingRows];
}

/**
 * Builds the publication rows for Instagram.
 */
function buildInstagramRows(pub: PortalPublication, getPortalLabel: (p: string) => string): PublicationRow[] {
  const portal = 'instagram';
  const { postIds } = resolveIds(pub);
  if (postIds.length === 0) {
    return pendingPortalRow(portal, pub, getPortalLabel, 'instagram-legacy');
  }
  const base = getPortalLabel(portal);
  return postIds.map((pid, i) => ({
    key: `instagram-post-${pid}`,
    portal,
    label: facebookLabel(base, postIds.length > 1, `${i + 1}`),
    status: pub.status,
    published_at: pub.published_at,
    post_id: pid,
    permalink: pub.permalink,
    ...leadAdFields(pub),
  }));
}

/**
 * Builds a generic single-row portal entry.
 */
function buildGenericRow(portal: string, pub: PortalPublication, getPortalLabel: (p: string) => string): PublicationRow {
  return {
    key: portal,
    portal,
    label: getPortalLabel(portal),
    status: pub.status,
    published_at: pub.published_at,
    post_id: pub.post_id,
    listing_id: pub.listing_id,
  };
}

/** True when a Meta (Facebook/Instagram) portal is disabled by its flag. */
function isMetaPortalDisabled(portal: string): boolean {
  const isMeta = portal === 'facebook' || portal === 'instagram';
  return isMeta && !FF_APP_FACEBOOK;
}

/** True when a feed portal (olx or another feed) is disabled by its flag. */
function isFeedPortalDisabled(portal: string): boolean {
  if (portal === 'olx') return !FF_APP_OLX;
  if (portal === 'chavesnamao') return !(FF_APP_CHAVES_NA_MAO || FF_APP_PORTAL_FEEDS);
  return PORTAL_KEYS.has(portal) && !FF_APP_PORTAL_FEEDS;
}

/**
 * Determines whether a portal is hidden by feature flags.
 */
function isPortalHidden(portal: string): boolean {
  if (isMetaPortalDisabled(portal)) return true;
  if (isFeedPortalDisabled(portal)) return true;
  return PORTAL_KEYS.has(portal) && !FF_APP_PORTALS_INTEGRATION;
}

const ROW_BUILDERS: Record<string, (pub: PortalPublication, getLabel: (p: string) => string) => PublicationRow[]> = {
  facebook: buildFacebookRows,
  instagram: buildInstagramRows,
};

/**
 * Builds all publication rows for the center list.
 */
export function buildRows(
  publications: Record<string, PortalPublication>,
  getPortalLabel: (p: string) => string,
): PublicationRow[] {
  const rows: PublicationRow[] = [];
  for (const [rawPortal, pub] of Object.entries(publications)) {
    const portal = actionPortalForIndex(rawPortal);
    if (isPortalHidden(portal)) continue;
    const builder = ROW_BUILDERS[portal];
    if (builder) rows.push(...builder(pub, getPortalLabel));
    else rows.push(buildGenericRow(portal, pub, getPortalLabel));
  }
  return rows;
}
